import { memo, useCallback, useEffect, useMemo } from 'react';
import { playButtonClickSound } from '@wg/web2clientapi/sound';
import Button from '@wg/wows-react-uikit/components/Button/Button';
import Guidingtip, {
  GUIDINGTIP_ALIGNS,
  GUIDINGTIP_POSITIONS,
  GUIDINGTIP_THEMES,
} from '@wg/wows-react-uikit/features/onboarding/components/Guidingtip/Guidingtip';
import useOnboarding from '@wg/wows-react-uikit/features/onboarding/hooks/useOnboarding';

import { ONBOARDING_GUIDES, ONBOARDING_GUIDES_CONFIG } from '~/const/onboarding';
import { HAS_DISCORD_SERVICE } from '~/constants';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { showClanInfoDialog, showInviteAcceptDialog, showSendApplicationDialog } from '~/helpers/dialogs';
import { t } from '~/helpers/localization';
import { getEntrySourceFromUrl } from '~/helpers/location';
import { hasPermission, PERMISSIONS } from '~/roles';
import { useAppDispatch } from '~/store';
import { hideDialog } from '~/store/slices/dialogsSlice';

import ButtonJoinClan from '~/UIKit/ButtonJoinClan/ButtonJoinClan';

import styles from './ClanHeader.module.scss';

import type { ClanInfoType } from '~/Actions/ActionClanProfile';
import type { ClanInvitationMinimal } from '~/Actions/ActionInvites';
import type { ICurrentAccountState } from '~/store/slices/currentAccountSlice';
import type { ClanPreloaded } from '~/types/declaration';

interface ClanHeaderActionsProps {
  clan: ClanInfoType | ClanPreloaded;
  currentAccount: ICurrentAccountState;
  isOwnClanBase: boolean;
}

const ClanHeaderActions = ({ clan, currentAccount, isOwnClanBase }: ClanHeaderActionsProps) => {
  const dispatch = useAppDispatch();

  const showJoinButton = !!currentAccount.id && !currentAccount.clanId;

  // About clan data
  const hasClanSocials = useMemo(() => {
    const urls = clan.communityUrls;
    if (!urls) {
      return false;
    }

    return Object.keys(urls).length > 0;
  }, [clan.communityUrls]);

  const isCanEditSocials = useMemo(() => {
    if (!isOwnClanBase) {
      return false;
    }

    return hasPermission(currentAccount.roleName, PERMISSIONS.CHANGE_SETTINGS);
  }, [isOwnClanBase, currentAccount.roleName]);

  // Onboarding
  const { isCurrentStepCompleted, completeCurrentStep } = useOnboarding({
    stepName: ONBOARDING_GUIDES.CLAN_ABOUT_DISCORD,
  });

  const guidingtipClansAboutDiscord = useMemo(() => {
    if (isCanEditSocials && !hasClanSocials && HAS_DISCORD_SERVICE) {
      return ONBOARDING_GUIDES_CONFIG.CLAN_ABOUT_DISCORD_MEMBER;
    }

    return ONBOARDING_GUIDES_CONFIG.CLAN_ABOUT_DISCORD_GUEST;
  }, [isCanEditSocials, hasClanSocials]);

  useEffect(() => {
    if (!isCurrentStepCompleted && isCanEditSocials && hasClanSocials) {
      completeCurrentStep();
    }
  }, [isCurrentStepCompleted, isCanEditSocials, hasClanSocials, completeCurrentStep]);

  // Actions handlers
  const handleAcceptInvite = useCallback(
    (invite: ClanInvitationMinimal) => {
      dispatch(hideDialog());
      dispatch(showInviteAcceptDialog(invite));
    },
    [dispatch],
  );

  const handleSendApplication = useCallback(
    (targetClan: ClanInfoType) => {
      const source = getEntrySourceFromUrl();
      dispatch(showSendApplicationDialog(targetClan, source));
    },
    [dispatch],
  );

  const handleClanInfoClick = useCallback(() => {
    dwhExport.push(DWH_EVENTS.SUPPLY.ABOUT_CLICK);

    void playButtonClickSound();

    const source = getEntrySourceFromUrl();
    dispatch(showClanInfoDialog({ clanId: clan.id, source, isOpenedFromClanBase: true }));

    if (!isCurrentStepCompleted) {
      completeCurrentStep();
    }
  }, [dispatch, clan.id, isCurrentStepCompleted, completeCurrentStep]);

  const handleOpenApplicationModal = useCallback(() => {
    dwhExport.push(DWH_EVENTS.CLAN_BASE.OPEN_APPLICATION_MODAL, {
      clan_id: clan.id,
    });
  }, [clan.id]);

  return (
    <>
      {showJoinButton ? (
        <ButtonJoinClan
          classNameBtnInner={styles.clanActionBtnInner}
          clan={clan}
          size={'isSmaller'}
          currentAccount={currentAccount}
          onAcceptInvite={handleAcceptInvite}
          onSendApplication={handleSendApplication}
          onClick={handleOpenApplicationModal}
        />
      ) : null}
      <div className={styles.clanActionBtnWrapper}>
        <Guidingtip
          name={guidingtipClansAboutDiscord.name}
          header={guidingtipClansAboutDiscord.header}
          content={guidingtipClansAboutDiscord.content}
          position={GUIDINGTIP_POSITIONS.BOTTOM}
          align={{
            0: GUIDINGTIP_ALIGNS.LEFT,
            1800: GUIDINGTIP_ALIGNS.CENTER,
          }}
          theme={GUIDINGTIP_THEMES.CLIENT}
          delay={1000}
          withBeacon
        >
          <Button
            className={styles.clanAction}
            classNameBtn={styles.clanActionBtn}
            classNameBtnInner={styles.clanActionBtnInner}
            type={'secondary'}
            isFlat
            isSmaller
            isJustified
            onClick={handleClanInfoClick}
          >
            {t('About clan')}
          </Button>
        </Guidingtip>
      </div>
    </>
  );
};

export default memo(ClanHeaderActions);
